import { useCallback, useEffect, useState } from "react";
import useApi from "./useApi";

const useBoxOrders = ({ storeId, receivedStatus = "PENDING", page = 1, pageSize = 10 }) => {
  const { request } = useApi();
  const [boxOrders, setBoxOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);

  const fetchBoxOrders = useCallback(
    async (signal) => {
      if (!storeId) {
        setBoxOrders([]);
        setIsLoading(false);
        setError(null);
        setTotal(0);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const data = await request({
          path: "/box/order",
          params: {
            storeId,
            receivedStatus,
            page,
            pageSize,
          },
          headers: { "X-Store-Id": storeId },
          signal,
        });

        const orders = Array.isArray(data?.boxOrders) ? data.boxOrders : [];
        setBoxOrders(orders);
        setTotal(Number.isFinite(data?.total) ? data.total : orders.length);
      } catch (err) {
        if (err?.name === "AbortError") return;
        setBoxOrders([]);
        setTotal(0);
        setError(err?.message || "Failed to load box orders.");
      } finally {
        setIsLoading(false);
      }
    },
    [request, storeId, receivedStatus, page, pageSize]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchBoxOrders(controller.signal);
    return () => controller.abort();
  }, [fetchBoxOrders]);

  const refetch = useCallback(() => {
    const controller = new AbortController();
    fetchBoxOrders(controller.signal);
    return () => controller.abort();
  }, [fetchBoxOrders]);

  // Map box order data to format expected by ShipmentsTable component
  const mappedOrders = boxOrders.map((order) => {
    // Convert boxTypeName from "BOX_TYPE_1" to "Type A", "BOX_TYPE_2" to "Type B", etc.
    let typeLabel = order.boxTypeName || "";
    if (typeLabel.includes("BOX_TYPE_1") || typeLabel === "A") {
      typeLabel = "Type A";
    } else if (typeLabel.includes("BOX_TYPE_2") || typeLabel === "B") {
      typeLabel = "Type B";
    } else if (typeLabel.includes("BOX_TYPE_3") || typeLabel === "C") {
      typeLabel = "Type C";
    } else {
      // Fallback: try to extract type from name
      const match = typeLabel.match(/TYPE[_\s]*([ABC123])/i);
      if (match) {
        typeLabel = `Type ${match[1].toUpperCase()}`;
      }
    }

    return {
      id: order.id,
      type: typeLabel,
      quantity: Number.isFinite(order.quantity) ? order.quantity : 0,
      trackingNo: order.trackingNumber || "—",
      created: order.createdAt || "",
      boxTypeName: order.boxTypeName,
      receivedStatus: order.receivedStatus,
      fulfillmentStatus: order.fulfillmentStatus,
    };
  });

  return {
    boxOrders,
    orders: mappedOrders,
    isLoading,
    error,
    total,
    refetch,
  };
};

export default useBoxOrders;
